import React, { ChangeEvent, useState } from 'react';
import Input from '../../../ui-elements/Input/Input';
import Checkbox from '../../../ui-elements/Input/Checkbox';
import Button from '../../../ui-elements/Button/Button';
import { faCirclePlus, faTrash } from '@fortawesome/pro-solid-svg-icons';
/**
 * Represents a single politically exposed person entry.
 * - ssn: Icelandic national ID (kennitala), not required for foreign individuals
 * - name: Full name of the individual
 * - job: The high-ranking public position they hold/held
 * - foreign: Whether this person is a foreign national (hides SSN field when true)
 */
interface PoliticalPerson {
    ssn: string;
    name: string;
    job: string;
    foreign: boolean;
}

/**
 * PoliticalDangerExample
 *
 * An interactive example component that demonstrates how users can register
 * politically exposed persons (PEPs) in a KYC questionnaire.
 *
 * Features:
 * - Add/remove multiple person entries
 * - "Foreign individual" checkbox that hides the SSN field with a smooth
 *   CSS animation (the SSN container collapses and the name field expands
 *   to fill the space)
 * - Validation: the "Add" button is disabled until all current entries
 *   have their required fields filled in
 * - Minimum of one entry is always shown (the remove button is disabled
 *   when only one entry exists)
 *
 * This mirrors the behavior of PoliticalDangerHandler from the KYC app,
 * but uses the local UI components (Input, Checkbox, Button) and does not
 * call any APIs for SSN lookup.
 */
const PoliticalDangerExample: React.FC = () => {
    /** The empty template used when adding a new person row */
    const emptyPerson: PoliticalPerson = { ssn: "", name: "", job: "", foreign: false };

    /** State holding all person entries in the list */
    const [persons, setPersons] = useState<PoliticalPerson[]>([{ ...emptyPerson }]);

    /**
     * Updates a single field on a specific person entry.
     * When the "foreign" checkbox is toggled ON, the SSN field is cleared
     * since foreign individuals don't have an Icelandic kennitala.
     */
    const handleChange = (index: number, field: keyof PoliticalPerson, value: string | boolean) => {
        const updated = [...persons];

        if (field === "foreign") {
            updated[index].foreign = value as boolean;
            // Clear SSN when marking as foreign — it's no longer relevant
            if (value === true) {
                updated[index].ssn = "";
            }
        } else {
            updated[index][field] = value as string;
        }

        setPersons(updated);
    };

    /**
     * Checks whether a single person entry has all required fields filled.
     * Foreign individuals only need name + job.
     * Domestic individuals also need SSN (kennitala).
     */
    const isPersonValid = (person: PoliticalPerson): boolean => {
        const hasName = person.name.trim() !== "";
        const hasJob = person.job.trim() !== "";

        if (person.foreign) {
            return hasName && hasJob;
        }

        const hasSSN = person.ssn.trim() !== "";
        return hasName && hasJob && hasSSN;
    };

    /**
     * Adds a new empty person row, but only if all existing entries
     * have valid data. This prevents users from stacking empty rows.
     */
    const addPerson = () => {
        if (persons.every(isPersonValid)) {
            setPersons([...persons, { ...emptyPerson }]);
        }
    };

    /**
     * Removes a person entry by index.
     * Will not remove the last remaining entry (minimum 1 required).
     */
    const removePerson = (index: number) => {
        if (persons.length <= 1) return;
        setPersons(persons.filter((_, i) => i !== index));
    };

    return (
        <div className="KYC_political-danger">
            {/* Header text prompting the user to specify which people and positions */}
            <p className="paragraph-small--bold">
                Vinsamlegast tilgreinið hvaða aðilar og hvaða störf:
            </p>

            {persons.map((person, index) => (
                <div key={index} className="political-danger-individual-wrapper">
                    {/* 
                        The 'is-foreign' class triggers CSS animations:
                        - SSN container collapses (flex: 0, opacity: 0)
                        - Name container expands with negative margin to fill the gap
                    */}
                    <div className={`political-danger-individual ${person.foreign ? 'is-foreign' : ''}`}>

                        {/* SSN (Kennitala) input — hidden via CSS animation when foreign */}
                        <div className="ssn-input-container">
                            <Input
                                label="Kennitala"
                                inputSize="lg"
                                value={person.ssn}
                                onChange={(e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) =>
                                    handleChange(index, "ssn", e.target.value)
                                }
                            />
                        </div>

                        {/* Name input — always visible, expands when SSN is hidden */}
                        <div className="name-input-container">
                            <Input
                                label="Nafn"
                                inputSize="lg"
                                value={person.name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) =>
                                    handleChange(index, "name", e.target.value)
                                }
                            />
                        </div>

                        {/* Job/position input — always visible, fixed width (1/3) */}
                        <div className="job-input-container">
                            <Input
                                label="Háttsett starf"
                                inputSize="lg"
                                value={person.job}
                                onChange={(e: React.ChangeEvent<HTMLInputElement & HTMLTextAreaElement>) =>
                                    handleChange(index, "job", e.target.value)
                                }
                            />
                        </div>

                        {/* Remove button — disabled when only one entry exists */}
                        <Button
                            buttonType="secondary"
                            icon={faTrash}
                            disabled={persons.length <= 1}
                            onClick={() => removePerson(index)}
                            size="lg"
                        />
                    </div>

                    {/* Foreign individual checkbox — toggles SSN visibility */}
                    <Checkbox
                        id={`foreign-individual-${index}`}
                        label="Erlendur aðili"
                        value={person.foreign ? "true" : "false"}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleChange(index, "foreign", e.target.checked)
                        }
                    />
                </div>
            ))}

            {/* Add person button — disabled until all current entries are valid */}
            <Button
                buttonType="secondary"
                size="sm"
                icon={faCirclePlus}
                onClick={addPerson}
                disabled={!persons.every(isPersonValid)}
            >
                Bæta við aðila
            </Button>
        </div>
    );
};

export default PoliticalDangerExample;